import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowLeft, Globe, RotateCcw, Info, Play, Pause,
  Heart, Zap, Activity, AlertTriangle, Volume2
} from 'lucide-react';

/**
 * THE 3D HEART PUMP - Interactive Cardiovascular Simulation
 * Students can block arteries, change heart rate, see blood flow
 */
const HeartPumpSimulation = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [language, setLanguage] = useState('en');
  const [showInfo, setShowInfo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [heartRate, setHeartRate] = useState(72);
  const [blockages, setBlockages] = useState({ leftCoronary: 0, rightCoronary: 0 });
  const [animationPhase, setAnimationPhase] = useState(0);
  const [bloodPressure, setBloodPressure] = useState({ systolic: 120, diastolic: 80 });
  const [oxygenLevel, setOxygenLevel] = useState(98);
  const [showLabels, setShowLabels] = useState(true);

  // Translations
  const t = {
    title: language === 'en' ? 'The 3D Heart Pump' : '3D हृदय पंप',
    subtitle: language === 'en' ? 'Interactive cardiovascular system' : 'इंटरैक्टिव हृदय प्रणाली',
    heartRate: language === 'en' ? 'Heart Rate' : 'हृदय गति',
    bpm: language === 'en' ? 'BPM' : 'धड़कन/मिनट',
    bloodPressure: language === 'en' ? 'Blood Pressure' : 'रक्तचाप',
    oxygenLevel: language === 'en' ? 'Oxygen Level' : 'ऑक्सीजन स्तर',
    blockages: language === 'en' ? 'Artery Blockages' : 'धमनी अवरोध',
    leftCoronary: language === 'en' ? 'Left Coronary' : 'बाईं कोरोनरी',
    rightCoronary: language === 'en' ? 'Right Coronary' : 'दाईं कोरोनरी',
    chambers: language === 'en' ? 'Heart Chambers' : 'हृदय कक्ष',
    rightAtrium: language === 'en' ? 'Right Atrium' : 'दाहिना आलिंद',
    rightVentricle: language === 'en' ? 'Right Ventricle' : 'दाहिना निलय',
    leftAtrium: language === 'en' ? 'Left Atrium' : 'बायां आलिंद',
    leftVentricle: language === 'en' ? 'Left Ventricle' : 'बायां निलय',
    aorta: language === 'en' ? 'Aorta' : 'महाधमनी',
    pulmonaryArtery: language === 'en' ? 'Pulmonary Artery' : 'फुफ्फुसीय धमनी',
    vena: language === 'en' ? 'Vena Cava' : 'महाशिरा',
    pulmonaryVein: language === 'en' ? 'Pulmonary Vein' : 'फुफ्फुसीय शिरा',
    systole: language === 'en' ? 'Systole (Contraction)' : 'संकुचन',
    diastole: language === 'en' ? 'Diastole (Relaxation)' : 'विश्राम',
    warning: language === 'en' ? 'Warning: High blockage detected!' : 'चेतावनी: उच्च अवरोध पाया गया!',
    reset: language === 'en' ? 'Reset' : 'रीसेट',
    showLabels: language === 'en' ? 'Show Labels' : 'लेबल दिखाएं',
    info: language === 'en' ? 'How it works' : 'यह कैसे काम करता है',
    infoText: language === 'en' 
      ? 'Watch how blood flows through the four chambers of the heart. Adjust the heart rate to see how valves work faster or slower. Block arteries to simulate heart disease and observe reduced blood flow!'
      : 'देखें कि रक्त हृदय के चार कक्षों से कैसे बहता है। वाल्व तेज या धीमी गति से कैसे काम करते हैं देखने के लिए हृदय गति समायोजित करें। हृदय रोग को अनुकरण करने के लिए धमनियों को अवरुद्ध करें और कम रक्त प्रवाह देखें!',
  };

  // Animation loop
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setAnimationPhase(p => (p + 1) % 100);
    }, 60000 / heartRate / 100);
    
    return () => clearInterval(interval);
  }, [isPlaying, heartRate]);

  // Calculate health metrics based on blockages
  useEffect(() => {
    const avgBlockage = (blockages.leftCoronary + blockages.rightCoronary) / 2;
    
    // Blood pressure increases with blockage
    const systolicBase = 120 + (avgBlockage * 0.5);
    const diastolicBase = 80 + (avgBlockage * 0.3);
    setBloodPressure({
      systolic: Math.round(systolicBase + Math.sin(animationPhase / 10) * 5),
      diastolic: Math.round(diastolicBase + Math.sin(animationPhase / 10) * 3)
    });
    
    // Oxygen decreases with blockage
    setOxygenLevel(Math.max(85, 98 - avgBlockage * 0.15));
  }, [blockages, animationPhase]);

  // Draw heart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // Clear
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, width, height);
    
    // Calculate pulse effect
    const pulsePhase = animationPhase / 100;
    const contractPhase = pulsePhase < 0.3 ? pulsePhase / 0.3 : pulsePhase < 0.5 ? 1 - (pulsePhase - 0.3) / 0.2 : 0;
    const pulseScale = 1 + contractPhase * 0.08;
    
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(pulseScale, pulseScale);
    ctx.translate(-centerX, -centerY);
    
    // Draw blood vessels (background)
    // Vena Cava (blue - deoxygenated)
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 20;
    ctx.beginPath();
    ctx.moveTo(centerX + 80, centerY - 120);
    ctx.quadraticCurveTo(centerX + 100, centerY - 80, centerX + 60, centerY - 40);
    ctx.stroke();
    
    // Pulmonary Artery (blue - deoxygenated to lungs)
    ctx.strokeStyle = '#6366f1';
    ctx.beginPath();
    ctx.moveTo(centerX - 30, centerY - 60);
    ctx.quadraticCurveTo(centerX - 80, centerY - 100, centerX - 120, centerY - 80);
    ctx.stroke();
    
    // Pulmonary Vein (red - oxygenated from lungs)
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 15;
    ctx.beginPath();
    ctx.moveTo(centerX - 120, centerY + 20);
    ctx.quadraticCurveTo(centerX - 80, centerY - 20, centerX - 40, centerY - 20);
    ctx.stroke();
    
    // Aorta (red - oxygenated)
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 25;
    ctx.beginPath();
    ctx.moveTo(centerX + 20, centerY - 40);
    ctx.quadraticCurveTo(centerX + 60, centerY - 100, centerX + 120, centerY - 120);
    ctx.stroke();
    
    // Draw heart shape (4 chambers)
    // Right Atrium (top right, blue)
    ctx.fillStyle = `rgba(59, 130, 246, ${0.6 + contractPhase * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(centerX + 50, centerY - 30, 45, 40, 0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Right Ventricle (bottom right, blue)
    ctx.fillStyle = `rgba(59, 130, 246, ${0.7 + contractPhase * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(centerX + 40, centerY + 50, 50, 55, 0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1e40af';
    ctx.stroke();
    
    // Left Atrium (top left, red)
    ctx.fillStyle = `rgba(239, 68, 68, ${0.6 + contractPhase * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(centerX - 40, centerY - 30, 40, 35, -0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#991b1b';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Left Ventricle (bottom left, red - thicker walls)
    ctx.fillStyle = `rgba(239, 68, 68, ${0.7 + contractPhase * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(centerX - 30, centerY + 50, 45, 60, -0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#991b1b';
    ctx.lineWidth = 4;
    ctx.stroke();
    
    // Draw septum (wall between left and right)
    ctx.fillStyle = '#64748b';
    ctx.beginPath();
    ctx.moveTo(centerX - 5, centerY - 60);
    ctx.quadraticCurveTo(centerX + 5, centerY + 20, centerX - 5, centerY + 100);
    ctx.lineWidth = 8;
    ctx.stroke();
    
    // Draw valves
    const valveOpenness = contractPhase;
    
    // Tricuspid valve (between RA and RV)
    ctx.fillStyle = '#f8fafc';
    ctx.beginPath();
    ctx.ellipse(centerX + 45, centerY + 5, 15, 5 + valveOpenness * 10, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Mitral valve (between LA and LV)
    ctx.beginPath();
    ctx.ellipse(centerX - 35, centerY + 5, 12, 5 + valveOpenness * 8, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Pulmonary valve
    ctx.beginPath();
    ctx.ellipse(centerX - 20, centerY - 55, 10, 4 + (1 - valveOpenness) * 6, -0.5, 0, Math.PI * 2);
    ctx.fill();
    
    // Aortic valve
    ctx.beginPath();
    ctx.ellipse(centerX + 10, centerY - 50, 10, 4 + (1 - valveOpenness) * 6, 0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw blood flow particles
    const numParticles = 20;
    for (let i = 0; i < numParticles; i++) {
      const progress = (animationPhase / 100 + i / numParticles) % 1;
      let px, py, color;
      
      if (progress < 0.25) {
        // Vena Cava to Right Atrium
        const t = progress / 0.25;
        px = centerX + 80 - t * 30;
        py = centerY - 120 + t * 90;
        color = '#60a5fa';
      } else if (progress < 0.5) {
        // Right side to Pulmonary
        const t = (progress - 0.25) / 0.25;
        px = centerX + 50 - t * 80;
        py = centerY - 30 - t * 50;
        color = '#818cf8';
      } else if (progress < 0.75) {
        // Pulmonary Vein to Left Atrium
        const t = (progress - 0.5) / 0.25;
        px = centerX - 120 + t * 80;
        py = centerY + 20 - t * 50;
        color = '#f87171';
      } else {
        // Left side to Aorta
        const t = (progress - 0.75) / 0.25;
        px = centerX - 30 + t * 60;
        py = centerY + 30 - t * 80;
        color = '#ef4444';
      }
      
      // Apply blockage effect
      const blockageEffect = progress < 0.5 ? blockages.rightCoronary : blockages.leftCoronary;
      if (Math.random() * 100 < blockageEffect) continue;
      
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Draw labels
    if (showLabels) {
      ctx.fillStyle = '#94a3b8';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      
      ctx.fillText(t.rightAtrium, centerX + 50, centerY - 75);
      ctx.fillText(t.rightVentricle, centerX + 45, centerY + 115);
      ctx.fillText(t.leftAtrium, centerX - 40, centerY - 70);
      ctx.fillText(t.leftVentricle, centerX - 30, centerY + 120);
      
      ctx.fillStyle = '#3b82f6';
      ctx.fillText(t.vena, centerX + 110, centerY - 130);
      ctx.fillText(t.pulmonaryArtery, centerX - 130, centerY - 90);
      
      ctx.fillStyle = '#ef4444';
      ctx.fillText(t.pulmonaryVein, centerX - 140, centerY + 10);
      ctx.fillText(t.aorta, centerX + 130, centerY - 100);
    }
    
    ctx.restore();
    
    // Draw phase indicator
    const phase = animationPhase < 40 ? t.systole : t.diastole;
    ctx.fillStyle = animationPhase < 40 ? '#ef4444' : '#3b82f6';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(phase, centerX, height - 20);
    
  }, [animationPhase, blockages, showLabels, t]);

  const resetSimulation = () => {
    setHeartRate(72);
    setBlockages({ leftCoronary: 0, rightCoronary: 0 });
  };

  const isWarning = blockages.leftCoronary > 50 || blockages.rightCoronary > 50;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/science')} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg animate-pulse">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{t.title}</h1>
              <p className="text-xs text-slate-400">{t.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setIsPlaying(!isPlaying)} className={`p-2 rounded-lg transition-colors ${isPlaying ? 'bg-green-600' : 'bg-slate-800'}`}>
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white" />}
            </button>
            <button onClick={() => setShowInfo(!showInfo)} className={`p-2 rounded-lg transition-colors ${showInfo ? 'bg-red-600' : 'bg-slate-800 hover:bg-slate-700'}`}>
              <Info className="w-5 h-5 text-white" />
            </button>
            <button onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
              className="flex items-center gap-1 px-3 py-2 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors">
              <Globe className="w-4 h-4 text-red-400" />
              <span className="text-xs text-slate-300">{language === 'en' ? 'हिन्दी' : 'ENG'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {showInfo && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
            <p className="text-sm text-red-200">{t.infoText}</p>
          </div>
        )}

        {isWarning && (
          <div className="mb-4 p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            <p className="text-sm text-yellow-200">{t.warning}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Controls */}
          <div className="space-y-4">
            {/* Heart Rate Control */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-red-400 flex items-center gap-2">
                    <Activity className="w-4 h-4" /> {t.heartRate}
                  </span>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-white">{heartRate}</span>
                    <span className="text-sm text-slate-400 ml-1">{t.bpm}</span>
                  </div>
                </div>
                <input type="range" min="40" max="180" value={heartRate} onChange={(e) => setHeartRate(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-red-500" />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>40</span>
                  <span>{language === 'en' ? 'Normal: 60-100' : 'सामान्य: 60-100'}</span>
                  <span>180</span>
                </div>
              </CardContent>
            </Card>

            {/* Blockage Controls */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-300 mb-4">{t.blockages}</h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400">{t.leftCoronary}</span>
                      <span className={`text-sm font-bold ${blockages.leftCoronary > 50 ? 'text-red-400' : 'text-green-400'}`}>
                        {blockages.leftCoronary}%
                      </span>
                    </div>
                    <input type="range" min="0" max="90" value={blockages.leftCoronary} 
                      onChange={(e) => setBlockages({...blockages, leftCoronary: parseInt(e.target.value)})}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-red-500" />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400">{t.rightCoronary}</span>
                      <span className={`text-sm font-bold ${blockages.rightCoronary > 50 ? 'text-red-400' : 'text-green-400'}`}>
                        {blockages.rightCoronary}%
                      </span>
                    </div>
                    <input type="range" min="0" max="90" value={blockages.rightCoronary}
                      onChange={(e) => setBlockages({...blockages, rightCoronary: parseInt(e.target.value)})}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vital Signs */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded-xl p-3 text-center">
                    <div className="text-xs text-slate-400 mb-1">{t.bloodPressure}</div>
                    <div className="text-xl font-bold text-white">
                      {bloodPressure.systolic}/{bloodPressure.diastolic}
                    </div>
                    <div className="text-xs text-slate-500">mmHg</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-3 text-center">
                    <div className="text-xs text-slate-400 mb-1">{t.oxygenLevel}</div>
                    <div className={`text-xl font-bold ${oxygenLevel < 95 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {oxygenLevel.toFixed(1)}%
                    </div>
                    <div className="text-xs text-slate-500">SpO₂</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button onClick={() => setShowLabels(!showLabels)} variant="outline" 
                className={`flex-1 border-slate-600 ${showLabels ? 'bg-slate-700' : ''}`}>
                {t.showLabels}
              </Button>
              <Button onClick={resetSimulation} variant="outline" className="flex-1 border-slate-600 text-slate-300">
                <RotateCcw className="w-4 h-4 mr-2" /> {t.reset}
              </Button>
            </div>
          </div>

          {/* Heart Visualization */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <canvas ref={canvasRef} width={600} height={500} className="w-full rounded-xl" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeartPumpSimulation;
